import { Box, Button, Stack, Tooltip } from '@mui/material'
import { observer } from 'mobx-react'
import moment from 'moment/moment'
import React, { FC } from 'react'
import { useIntl } from 'react-intl'
import DataUnit from '../../../../../components/customElements/DataUnit/DataUnit'
import EquipmentCheckingField from '../../../../../components/FormElements/EquipmentCheckingField'
import FieldError from '../../../../../components/FormElements/FieldError'
import FilterDatePicker from '../../../../../components/FormElements/FilterDatePicker/FilterDatePicker'
import FormReceiverAutocomplete from '../../../../../components/FormElements/FormReceiverAutocomplete'
import { getImageSrc } from '../../../../../helpers/getImageSrc'
import { formatSN } from '../../../../../utils/utils/formatSN'
import FormSelect from '../../../../SingleRequestPage/components/SingleRequestDrawer/elements/FormSelect'
import TypedAccordion from '../../../../SingleRequestPage/components/SingleRequestDrawer/elements/TypedAccordion'
import { RequestType } from '../../../../SingleRequestPage/types/Request.type'
import { newRequestDrawerStore } from '../../../stores/NewRequestDrawerStore'
import NewPhotosUpload from './NewPhotosUpload'
import { DefaultImageItem } from './NewPhotoUploadForm'
import NewUploadedPhotos from './NewUploadedPhotos'

interface InstallationDataAccProps {
  request: RequestType
  store: any
  photoPermission: any
  files: DefaultImageItem[]
  form: any
  isValid: boolean
  watch: any
  isEquipmentIdChecked: boolean
  deleteImage: (id: number) => void
  handleSetFiles: (newFiles: FileList) => void
  deleteFile: (file: DefaultImageItem) => void
}

const InstallationDataAcc: FC<InstallationDataAccProps> = ({
  request,
  photoPermission,
  files,
  form,
  isValid,
  deleteImage,
  watch,
  handleSetFiles,
  deleteFile,
  isEquipmentIdChecked,
}) => {
  const intl = useIntl()
  const {
    onReset,
    isPending,
    isAtLeastOneChanged,
    isEditMode,
    dateError,
    canUploadImages,
    defaultValues,
    control,
    canEditEquipmentId,
    canEditData,
    setIsEditMode,
    onSubmit,
    showOnlyPhotos,
    receiveresModels,
    canEditReceiver,
    errors,
    setValue,
    onCheckEquipment,
    lastCheckedId,
    setError,
  } = form

  const newIsValid = request.receiverSerialNumber
    ? isValid
    : !errors.installationDate &&
      !errors.equipmentId &&
      !errors.smartCardId &&
      isEquipmentIdChecked

  return (
    <TypedAccordion
      defaultOpenState={[5, 6].includes(request.status)}
      summary={'Данные установки'}
      onEdit={
        canEditData ? () => setIsEditMode((prev: boolean) => !prev) : undefined
      }
    >
      <Stack component={'form'}>
        {!isEditMode && (
          <>
            {!showOnlyPhotos && (
              <>
                <Box mt={2}>
                  <DataUnit
                    name='ID оборудования'
                    value={request.equipmentId}
                  />
                </Box>
                <Box mt={2}>
                  <DataUnit
                    name='Дата установки'
                    value={
                      request.installationDate
                        ? moment(request.installationDate).format('DD-MM-YYYY')
                        : '-'
                    }
                  />
                </Box>
                <Box mt={2}>
                  <DataUnit
                    name='S/N оборудования'
                    value={formatSN(request.receiverSerialNumber)}
                  />
                </Box>
                <Box mt={2}>
                  <DataUnit
                    name='Модель оборудования'
                    value={
                      request.receiverProduct ? request?.receiverProduct : '-'
                    }
                  />
                </Box>
              </>
            )}
            <NewUploadedPhotos
              request={request}
              images={newRequestDrawerStore.images}
              deleteImage={deleteImage}
            />
          </>
        )}
        {isEditMode && (
          <Stack spacing={'16px'} mt={2}>
            {canEditEquipmentId ? (
              <EquipmentCheckingField
                control={control}
                defaultEquipmentId={request?.equipmentId ?? ''}
                equipmentId={watch('equipmentId')!}
                lastCheckedId={lastCheckedId ?? ''}
                onChecked={onCheckEquipment}
                error={!!errors.equipmentId}
                withLabel
              />
            ) : (
              <DataUnit name='ID оборудования' value={request.equipmentId} />
            )}
            {canEditReceiver ? (
              <>
                {watch('hasCas') && (
                  <DataUnit
                    name='S/N оборудования'
                    value={formatSN(watch('receiverSerialNumber'))}
                  />
                )}
                {!watch('hasCas') && (
                  <FormReceiverAutocomplete
                    control={control}
                    name='receiverSerialNumber'
                    fullWidth
                    label='S/N оборудования'
                    setValue={setValue}
                    modelName='receiverModelId'
                    setError={setError}
                  />
                )}
              </>
            ) : (
              <DataUnit
                name='S/N оборудования'
                value={formatSN(request.receiverSerialNumber)}
              />
            )}
            {watch('smartCardId') && (
              <FieldError msg='К данному серийному номеру привязана другая smart карта. Проверьте данные или обратитесь к администратору!' />
            )}
            {canEditReceiver ? (
              <>
                {watch('hasCas') && (
                  <DataUnit
                    name='Модель оборудования'
                    value={watch('receiverModelId')}
                  />
                )}
                {!watch('hasCas') && (
                  <FormSelect
                    control={control}
                    fullWidth
                    name='receiverModelId'
                    label='Модель оборудования'
                    placeholder=''
                    items={
                      receiveresModels.map(
                        (el: any) => `${el.manufacturerName} - ${el.name}`,
                      ) ?? []
                    }
                    withLabel
                    disabled
                  />
                )}{' '}
              </>
            ) : (
              <DataUnit
                name='Модель оборудования'
                value={request.receiverProduct ? request.receiverProduct : '-'}
              />
            )}
            <Stack direction={'row'} spacing={'10px'}>
              {dateError && (
                <Tooltip
                  title={
                    'Дату установки можно отредактировать только в рамках отчётного периода текущего отчёта'
                  }
                >
                  <img
                    src={getImageSrc('tooltip')}
                    alt={''}
                    style={{
                      width: '18px',
                      marginTop: 0,
                      marginBottom: '-4px',
                    }}
                  />
                </Tooltip>
              )}
              <FilterDatePicker
                control={control}
                name={'installationDate'}
                label='Дата установки'
                defaultValue={defaultValues.installationDate}
              />
            </Stack>
            {canUploadImages ? (
              <NewPhotosUpload
                request={request}
                photoSettings={photoPermission}
                title={intl.formatMessage(
                  {
                    id: `Загрузите от {minimumLimit} до {maximumLimit} фотографий`,
                    defaultMessage: `Загрузите от {minimumLimit} до {maximumLimit} фотографий`,
                  },
                  {
                    minimumLimit: photoPermission?.minLimitPerLoad,
                    maximumLimit: photoPermission?.maxLimitPerLoad,
                  },
                )}
                collection={files ?? []}
                setFile={handleSetFiles}
                deleteFile={deleteFile}
              />
            ) : (
              <NewUploadedPhotos
                request={request}
                images={newRequestDrawerStore.images}
                deleteImage={deleteImage}
              />
            )}
          </Stack>
        )}
        {request.status !== 5 && isEditMode && (
          <>
            <Box className={'jst-sa'} sx={{ gap: '8px' }}>
              {request.status !== 6 && (
                <Button
                  type='button'
                  disabled={
                    !newIsValid ||
                    !isAtLeastOneChanged ||
                    dateError ||
                    !!errors.receiverSerialNumber
                  }
                  color={'primary'}
                  variant={'contained'}
                  fullWidth
                  loading={isPending}
                  onClick={() => onSubmit(watch())}
                >
                  {intl.formatMessage({
                    id: 'Применить изменения',
                  })}
                </Button>
              )}
              {request.status === 6 && (
                <Button
                  disabled={!newIsValid || !isAtLeastOneChanged || dateError}
                  color={'primary'}
                  onClick={() => onSubmit(watch())}
                  variant={'contained'}
                  fullWidth
                  loading={isPending}
                >
                  {intl.formatMessage({
                    id: 'Применить изменения',
                  })}
                </Button>
              )}
              <Button
                onClick={onReset}
                color={'primary'}
                variant={'contained'}
                fullWidth
              >
                {intl.formatMessage({ id: 'Отмена' })}
              </Button>
            </Box>
          </>
        )}
      </Stack>
    </TypedAccordion>
  )
}

export default observer(InstallationDataAcc)
